import { useEffect } from "react";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { z } from "zod";
import { localStore, memoryStore, useStore } from "../../../lib/store";
import { growthbookIdentityStore } from "../../../tools/growthbook";
import { signupSessionUidStore } from "../../auth/stores/signupSessionStore";
import {
  type CurrentUser,
  useCurrentUser,
} from "../../user/hooks/useCurrentUser";

/**
 * Where a user LIVES — the answer the root redirect, the post-login exit and
 * the notification router all ask for.
 *
 * Two destinations, and the room one CARRIES its room: a "pinned-room" answer
 * with no room to go to is not a state anything has to handle, because it
 * cannot be built.
 */
export type HomeDestination =
  | { readonly kind: "gpt" }
  | { readonly kind: "pinned-room"; readonly roomId: string };

/**
 * The destination, plus the state where it is not known yet.
 *
 * `pending` is a real state, not a quieter "pinned-room": flags read `false`
 * until the GrowthBook payload lands, and before the signed-in user's
 * attributes are synced they are bucketed on the anonymous ones
 * (`.knowledge/growthbook-oneshot-flag-race.md`). Both hazards resolve to the
 * room — today's answer — and are indistinguishable from a legitimate one. The
 * landing is a REDIRECT, the one shape that knowledge entry calls out: decided
 * early it cannot be taken back, so the callers hold instead of guessing.
 */
export type HomeSurface = { readonly kind: "pending" } | HomeDestination;

/**
 * Room members land on JuisciGPT instead of their pinned room. Targeted per
 * room from the GrowthBook console on the `pinned_room` attribute published by
 * `App` — one rule at 100% of its target, the way the practice rollout is run.
 *
 * The flag OFF is the historical behaviour, so a CDN outage (which resolves
 * every flag `false`, see `App.initialize`) degrades to it.
 */
const GPT_HOME_FLAG = "GPT_HOME_FOR_ROOM_MEMBERS";

/**
 * A member of Juisci-owned rooms only lands on JuisciGPT, except in the session
 * that created their account: that one opens the room they joined through.
 * No flag decides it, so it needs no GrowthBook payload.
 */
function isJuisciRoomsMemberPastSignup(
  user: {
    readonly uid: string;
    readonly roomsOwner: CurrentUser["roomsOwner"];
  },
  signupSessionUid: string | null
): boolean {
  return user.roomsOwner === "juisci" && signupSessionUid !== user.uid;
}

/** Pure so the rule is assertable without a React harness. */
export function resolveHomeSurface(input: {
  readonly user: {
    readonly uid: string;
    readonly pinnedRoom: string | null;
    readonly roomsOwner: CurrentUser["roomsOwner"];
  } | null;
  /** The account created in this session (`signupSessionUidStore`). */
  readonly signupSessionUid: string | null;
  readonly featurePayloadReady: boolean;
  /** The user GrowthBook currently holds attributes for. */
  readonly syncedIdentity: string | null;
  readonly isGptHomeForRoomMembers: boolean;
}): HomeSurface {
  // No user, no pin knowable; no pin, no question. Both answered BEFORE the
  // GrowthBook conditions on purpose: 59% of accounts have no pinned room, and
  // making them wait on a CDN payload to be told what their own user row
  // already says would put a network hop in front of every boot.
  if (input.user === null) return { kind: "gpt" };
  const pinnedRoom = input.user.pinnedRoom;
  if (pinnedRoom === null) return { kind: "gpt" };
  if (isJuisciRoomsMemberPastSignup(input.user, input.signupSessionUid)) {
    return { kind: "gpt" };
  }

  if (!input.featurePayloadReady) return { kind: "pending" };
  if (input.syncedIdentity !== input.user.uid) return { kind: "pending" };

  return input.isGptHomeForRoomMembers
    ? { kind: "gpt" }
    : { kind: "pinned-room", roomId: pinnedRoom };
}

/**
 * The settled answer for the signed-in user, republished for the callers that
 * are NOT in React — `getUserStartingPage` runs from the post-login redirect
 * and from a notification tap, neither of which can hold a hook.
 *
 * This is the shape `.knowledge/growthbook-oneshot-flag-race.md` prescribes:
 * ONE reactive read, published here, rather than an imperative
 * `growthbook.isOn()` in each of those modules.
 */
const settledHomeSurfaceStore = memoryStore<{
  readonly uid: string;
  readonly kind: HomeDestination["kind"];
} | null>(null);

/**
 * The last settled answer for THIS device, for the boot skeleton alone.
 *
 * The skeleton must pick a silhouette before anything is known — that is what a
 * skeleton is for — and the pathname cannot tell it, because both destinations
 * are reached from `/`. Persisting the previous answer makes the second boot
 * onwards paint the right one; the first, and a flag flipped since, paint the
 * room and land on JuisciGPT a beat later.
 *
 * A stale value can therefore cost a wrong SILHOUETTE and never a wrong
 * landing: the redirect below waits for the real answer either way.
 */
const lastBootDestinationStore = localStore<HomeDestination["kind"]>(
  "last-boot-home-destination",
  "pinned-room",
  z.enum(["gpt", "pinned-room"])
);

/**
 * The destination the boot skeleton should draw for a root boot, from the
 * persisted user cache alone (`currentUser` has not resolved yet).
 */
export function getCachedBootDestination(
  cachedPinnedRoom: string | null
): HomeDestination["kind"] {
  if (cachedPinnedRoom === null) return "gpt";
  return lastBootDestinationStore.get();
}

/**
 * The home surface for a signed-in reader, read REACTIVELY so it settles on the
 * real value when the payload arrives instead of being captured at mount.
 *
 * Also the single publisher of both stores above. Mounted more than once it
 * writes the same answer twice, which is why the effect keys on primitives:
 * the write happens when the ANSWER changes, not when a render does.
 */
export function useHomeSurface(): HomeSurface {
  const growthbookClient = useGrowthBook();
  const { currentUser } = useCurrentUser();
  const [identity] = useStore(growthbookIdentityStore);
  const isGptHomeForRoomMembers = useFeatureIsOn(GPT_HOME_FLAG);
  const [signupSessionUid] = useStore(signupSessionUidStore);

  const surface = resolveHomeSurface({
    user:
      currentUser === null
        ? null
        : {
            uid: currentUser.uid,
            pinnedRoom: currentUser.pinnedRoom,
            roomsOwner: currentUser.roomsOwner,
          },
    signupSessionUid,
    featurePayloadReady: growthbookClient.ready,
    // A failed sync leaves the cohort unstable, so it stays pending.
    syncedIdentity:
      identity !== null && identity.status === "synced" ? identity.uid : null,
    isGptHomeForRoomMembers,
  });

  const currentUid = currentUser === null ? null : currentUser.uid;
  const settledKind = surface.kind === "pending" ? null : surface.kind;

  useEffect(() => {
    // Nothing to publish for a signed-out visitor: the stores are read for a
    // named uid, and overwriting the device's last destination here would make
    // a logged-out boot repaint the next signed-in one.
    if (currentUid === null) return;
    if (settledKind === null) return;
    settledHomeSurfaceStore.set({ uid: currentUid, kind: settledKind });
    lastBootDestinationStore.set(settledKind);
  }, [currentUid, settledKind]);

  return surface;
}

/**
 * The destination for a caller that cannot hold — the "Not now" of a profiling
 * screen must not wait on a CDN payload to close.
 *
 * `pending` collapses to the historical answer, the room. It is nearly
 * unreachable at those call sites (the screen is walked seconds after boot,
 * long after the payload lands), and being wrong there costs one landing in the
 * user's own room, which their next visit corrects. The root redirect gets the
 * other treatment because it races on every single boot.
 */
export function settleHomeDestination(
  surface: HomeSurface,
  user: { readonly pinnedRoom: string | null }
): HomeDestination {
  if (surface.kind !== "pending") return surface;
  return user.pinnedRoom === null
    ? { kind: "gpt" }
    : { kind: "pinned-room", roomId: user.pinnedRoom };
}

/**
 * How long an imperative caller waits for the answer before taking the room.
 *
 * A ceiling and not a hope: `initGrowthbook()` carries no timeout, so an
 * awaited flag read can wedge a post-login redirect for as long as the CDN
 * hangs. Past this the user goes where they went before this feature existed.
 */
const HOME_SURFACE_DEADLINE_MS = 2000;

/**
 * The destination for a caller with no React context — the post-login exit, a
 * notification tap, the room-preview OAuth callback.
 *
 * Resolves at once for a user with no pin or with Juisci rooms only (no flag
 * decides those) and for one whose answer `useHomeSurface` has already
 * published; otherwise it waits for that publication, bounded by the deadline
 * above.
 */
export function waitForHomeDestination(user: {
  readonly uid: string;
  readonly pinnedRoom: string | null;
  readonly roomsOwner: CurrentUser["roomsOwner"];
}): Promise<HomeDestination> {
  const pinnedRoom = user.pinnedRoom;
  if (pinnedRoom === null) return Promise.resolve({ kind: "gpt" });
  if (isJuisciRoomsMemberPastSignup(user, signupSessionUidStore.get())) {
    return Promise.resolve({ kind: "gpt" });
  }

  // An arrow bound AFTER the guard, not a hoisted declaration: hoisting would
  // put the body before the narrowing and lose `pinnedRoom`'s non-null type.
  const toDestination = (kind: HomeDestination["kind"]): HomeDestination =>
    kind === "gpt"
      ? { kind: "gpt" }
      : { kind: "pinned-room", roomId: pinnedRoom };

  const published = settledHomeSurfaceStore.get();
  if (published !== null && published.uid === user.uid) {
    return Promise.resolve(toDestination(published.kind));
  }

  return new Promise((resolve) => {
    // Still null while `subscribe` runs, and never after: the store emits only
    // from `set`, so the listener cannot fire before the timer below is armed.
    let timer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = settledHomeSurfaceStore.subscribe((value) => {
      if (value === null || value.uid !== user.uid) return;
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
      resolve(toDestination(value.kind));
    });

    timer = setTimeout(() => {
      unsubscribe();
      resolve(toDestination("pinned-room"));
    }, HOME_SURFACE_DEADLINE_MS);
  });
}
